import { WorkEmpty } from '../work-page.tsx';
import { Alert, LoadingBlock } from '../ui.tsx';
import './payment-surfaces.css';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { paymentApi } from '../../api/endpoints.ts';
import { useSession } from '../../state/session.tsx';
import { usePlanFeature } from '../plan-gate.tsx';
import { StarterNudge } from '../starter-nudge.tsx';
import { QuotePaymentBadge } from './quote-payments.tsx';
import { formatCents } from '@shared/calculations/pricing.ts';
import { getDateLocale } from '../../i18n/locale.ts';

/**
 * What is still owed on accepted one-time quotes, on the dashboard: the
 * total, and the quotes behind it with the money that has waited longest
 * first.
 *
 * Outside the reports on purpose. On the Free plan this is the whole view of
 * what the company is owed; on a paid plan the visits a quote was booked into
 * are owed through their invoices instead, which the Money owed report
 * already counts — so the quote total and that report never count one job
 * twice, and the card says where the rest is.
 */
export function OwedCard({
  scope = 'location',
}: {
  /**
   * The dashboard's own scope: the working location, or every location the
   * viewer has, so the card adds up the same places as the figures beside it.
   */
  scope?: 'location' | 'all';
}) {
  const { t } = useTranslation();
  const { can, company, currentLocation, switchLocation } = useSession();
  const navigate = useNavigate();
  const hasReports = usePlanFeature('reports_core');
  const owed = useQuery({
    queryKey: ['owed', scope],
    queryFn: () => paymentApi.owed(undefined, scope === 'all' ? 'all' : undefined),
    enabled: can('payments:read'),
  });

  if (!can('payments:read')) return null;
  const data = owed.data;
  const money = (cents: number) => formatCents(cents, data?.currencyCode ?? 'USD', getDateLocale());

  return (
    <section className="card work-section payment-leaf" aria-labelledby="owed-heading">
      <div className="work-section-heading">
        <h2 id="owed-heading" className="text-lg font-semibold">
          {t('owedCard.title')}
        </h2>
      </div>
      {owed.isLoading ? (
        <LoadingBlock label={t('owedCard.loading')} />
      ) : owed.isError || !data ? (
        <Alert tone="error">{t('owedCard.loadError')}</Alert>
      ) : data.count === 0 ? (
        <WorkEmpty title={t('owedCard.nothingOwed')} />
      ) : (
        <>
          <p className="mt-1 text-2xl font-bold tabular">{money(data.totalCents)}</p>
          <p className="text-sm text-[color:var(--color-muted)]">{t('owedCard.hint', { count: data.count })}</p>
          <ul className="mt-3 divide-y divide-[color:var(--color-line)] text-sm">
            {data.quotes.map((quote) => {
              // A quote opens in its own location, which an all-locations list
              // can hold another of. Its link names that location, so a new
              // tab or window switches there itself (the quote page reads
              // `?location=`); a plain click moves the session there first.
              const to = `/quotes/${quote.quoteId}`;
              const elsewhere =
                quote.locationId && currentLocation && quote.locationId !== currentLocation.id
                  ? quote.locationId
                  : null;
              const link = elsewhere ? `${to}?location=${encodeURIComponent(elsewhere)}` : to;
              return (
                <li key={quote.quoteId} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <Link
                    to={link}
                    className="text-[color:var(--color-brand)] hover:underline"
                    onClick={(event) => {
                      // A middle click or a modified one follows the link to a
                      // new tab or window, as any link does.
                      const modified = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
                      if (!elsewhere || event.button !== 0 || modified) return;
                      event.preventDefault();
                      void switchLocation(elsewhere).then(
                        () => navigate(to),
                        // The quote page tries the switch again, and says so if it cannot.
                        () => navigate(link),
                      );
                    }}
                  >
                    {quote.quoteNumber}
                    {quote.customerName ? ` · ${quote.customerName}` : ''}
                  </Link>
                  <span className="flex items-center gap-2">
                    <QuotePaymentBadge status={quote.status} />
                    <span className="tabular font-medium">{money(quote.balanceCents)}</span>
                  </span>
                </li>
              );
            })}
          </ul>
          {data.count > data.quotes.length ? (
            <p className="mt-2 text-sm text-[color:var(--color-muted)]">
              {t('owedCard.more', { count: data.count - data.quotes.length })}
            </p>
          ) : null}
        </>
      )}
      {hasReports ? (
        <p className="mt-3 text-sm text-[color:var(--color-muted)]">
          {t('owedCard.visitsElsewhere')}{' '}
          <Link
            to={company?.reportsHubRoutes ? '/reports/receivables' : '/reports'}
            className="text-[color:var(--color-brand)] hover:underline"
          >
            {t('owedCard.visitsLink')}
          </Link>
        </p>
      ) : (
        <StarterNudge id="owed-card" message={t('owedCard.starterNudge')} className="mt-3" />
      )}
    </section>
  );
}
